import type { DebtData } from '@/types';

/** Match shared DEFAULT_CURRENCY — keep local to avoid test-time package resolution. */
const FALLBACK_CURRENCY = 'EUR';

const UNPAID_JOURNEY_NODES = ['PENDING', 'READY_FOR_OUTREACH', 'AWAITING_RESPONSE', 'ENGAGED'];

/**
 * A debt is "unpaid" — i.e. eligible for direct payment — when nothing holds it
 * (no active plan, and no scheduled single payment, which is a one-installment
 * plan) and its journey is still live. Shared between the Dashboard "Pay all"
 * CTA gate and the AccountPayment surface so the two stay in lockstep.
 *
 * Node-independent on the plan arm: nothing moves a case to PAYING when a plan is
 * created — that rides a payment event — so a future-dated plan sits at an outreach node.
 */
export function isUnpaidDebt(d: DebtData): boolean {
  if (d.paymentPlan) return false;
  return isLiveJourneyNode(d);
}

/** Journey still running — an outreach node or PAYING. */
function isLiveJourneyNode(d: Pick<DebtData, 'journeyNode'>): boolean {
  const node = (d.journeyNode || '').toUpperCase();
  return UNPAID_JOURNEY_NODES.includes(node) || node === 'PAYING';
}

/**
 * A debt the account-level surface counts — wider than {@link isUnpaidDebt} because it
 * includes arranged ones. The "Pay all" CTA and the account page's redirect gate ask how
 * many debts the account has, not how many are payable: an arranged case still needs the
 * surface to open so it can say why account-wide payment is barred.
 */
export function isOpenDebt(d: DebtData): boolean {
  return isLiveJourneyNode(d) && !isClearedDebt(d);
}

/**
 * Whether a debt can be paid right now: unpaid, and with something left to settle.
 *
 * Balance is part of it because a debt settles in the ledger before its journey node
 * follows, so an unpaid node can already be cleared — and the payment page has nothing
 * to offer for one.
 */
export function isPayableDebt(d: DebtData): boolean {
  return isUnpaidDebt(d) && d.currentBalance > 0;
}

export const COMPLETED_JOURNEY_NODES = ['CUSTOMER_RESOLVED', 'RESOLVED', 'WITHDRAWN', 'RETURNED'];

/**
 * Whether a debt should read as cleared: a resolved journey node, or a balance the
 * ledger has already settled.
 *
 * The balance arm is what makes a full payment land in one step. The node moves on an
 * async journey event ~15s after the payment reaches the ledger, so grouping on the node
 * alone leaves a paid-off debt sitting under Open Debts at €0.00 until the customer
 * reloads. A negative balance is an overpayment, not a settlement — it keeps its own
 * "contact support" treatment.
 */
export function isClearedDebt(d: DebtData): boolean {
  const node = (d.journeyNode || '').toUpperCase();
  return COMPLETED_JOURNEY_NODES.includes(node) || (d.originalAmount > 0 && d.currentBalance === 0);
}

export type CurrencyBalance = { currency: string; total: number };

/** Sum balances per ISO currency — never silently cross-sum. */
export function balancesByCurrency(
  debts: Pick<DebtData, 'currency' | 'currentBalance'>[]
): CurrencyBalance[] {
  const totals = new Map<string, number>();
  for (const d of debts) {
    const currency = (d.currency || FALLBACK_CURRENCY).toUpperCase();
    totals.set(currency, (totals.get(currency) ?? 0) + d.currentBalance);
  }
  return Array.from(totals, ([currency, total]) => ({ currency, total }));
}

/**
 * Debts included in the Connect account-payment snapshot: positive balance and
 * journey not closed. Broader than {@link isUnpaidDebt} — active-plan PAYING
 * cases still contribute to the mixed-currency gate.
 */
export function isAccountPaySnapshotDebt(
  d: Pick<DebtData, 'currentBalance' | 'journeyStatus'>
): boolean {
  return d.currentBalance > 0 && (d.journeyStatus || '').toLowerCase() !== 'closed';
}

/** True when positive-balance debts span more than one currency. */
export function isMultiCurrencyAccount(
  debts: Pick<DebtData, 'currency' | 'currentBalance'>[]
): boolean {
  return balancesByCurrency(debts.filter((d) => d.currentBalance > 0)).length > 1;
}

/**
 * Whether `debtId` is the account's only debt still carrying a balance — payable
 * now, or under an active payment plan.
 *
 * Gates single-debt surfaces from serving the account-wide Multibanco balance
 * reference: that code is amount-bound to the account total, so offering it
 * beside one debt of several would hand the customer a code for more than they
 * were shown.
 *
 * Deliberately counts plan-covered debts, unlike {@link isUnpaidDebt}. Whether a
 * plan-covered debt lands in the reference is a per-client server decision
 * (`balance_reference_includes_arranged_cases`), so a predicate that ignored
 * them would answer "this debt is the whole balance" for an account where the
 * server disagrees — and the resulting code cannot be retracted.
 */
export function isOnlyOpenDebt(debts: DebtData[], debtId: string | null | undefined): boolean {
  if (!debtId) return false;
  const open = debts.filter(isLiveJourneyNode);
  return open.length === 1 && open[0]?.id === debtId;
}
